import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ds2'
import { RevisionCard } from './RevisionCard'
import { revisionResultText, type OpenedRevision, type RevisionChoice } from './uploadFlow'

interface RevisionReviewProps {
    revision: OpenedRevision
    /** Tells the agent what came of the revision (the store's `notify`, held while a question or a turn waits). A host that shows the
     *  outcome itself (the review drawer) passes none: nothing is said to the chat. */
    onSaid?: (message: string) => void
    /** The user's choice, for a host that follows it in place (the drawer reads the load again). */
    onChoice?: (choice: RevisionChoice) => void
    onDone: () => void
}

/**
 * A rate con attached in the composer that the board filed as a revision of a load it already had: the board's own review, above the
 * composer, until the user accepts it, keeps the original or closes it. The message that carried the file already told the agent the
 * revision waits for review, so a close says nothing more; a choice is told once.
 */
export const RevisionReview = ({ revision, onSaid, onChoice, onDone }: RevisionReviewProps) => {
    const [submitting, setSubmitting] = useState(false)

    const resolved = (choice: RevisionChoice) => {
        if (choice.kind !== 'left') onSaid?.(revisionResultText(revision, choice))
        onChoice?.(choice)
        onDone()
    }

    return (
        <div
            role="group"
            aria-label={`Review the revision of load #${revision.loadNumber}`}
            className="flex min-w-0 max-w-full flex-col gap-2 overflow-hidden rounded-xl bg-surface p-3 shadow-surface"
        >
            <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-xs text-foreground-muted">
                    {revision.fileName} is a revision of load #{revision.loadNumber}
                </span>
                <Button size="sm" variant="ghost" iconOnly aria-label="Close" disabled={submitting} onClick={onDone}>
                    <X className="h-4 w-4" />
                </Button>
            </div>
            <RevisionCard revision={revision} onResolved={resolved} onSubmittingChange={setSubmitting} />
        </div>
    )
}
